// app/src/dashboard/widgets/chart/useChartEngine.ts
import { useEffect, useRef, useState } from "react";
import {
  createChart,
  IChartApi,
  ISeriesApi,
  CandlestickSeries,
  ColorType,
  CrosshairMode,
} from "lightweight-charts";
import { chartColors } from "./chartColors";

interface UseChartEngineOptions {
  isDark: boolean;
  pricePrecision: number;
  // Active theme id — rebuild the chart on ANY theme switch so it re-reads the
  // palette's CSS-variable colors (isDark alone misses dark→jungle, etc.).
  themeId?: string;
}

interface CandleBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export function useChartEngine(
  containerRef: React.RefObject<HTMLDivElement | null>,
  data: CandleBar[],
  options: UseChartEngineOptions
) {
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  // Incremented each time the series is recreated so useTradeMarkers can react to it
  const [seriesVersion, setSeriesVersion] = useState(0);
  const { isDark, pricePrecision, themeId } = options;

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return;
    const host = containerRef.current;

    // Destroy any previous instance (defensive — cleanup normally already did).
    if (chartRef.current) {
      try { chartRef.current.remove(); } catch { /* already disposed */ }
      chartRef.current = null;
      seriesRef.current = null;
    }

    // DOM-ownership isolation. lightweight-charts appends its own <canvas>/<table>
    // nodes INTO whatever element we hand createChart(), and rips them back out on
    // remove() — outside React's knowledge. If we pass the React-rendered
    // container directly, React's unmount (commitDeletionEffects) later tries to
    // removeChild() a node lightweight-charts already detached, throwing
    // "NotFoundError: Failed to execute 'removeChild'" mid-commit, which corrupts
    // reconciliation and trips the page error boundary (the crash users hit on
    // navigation / layout edit / theme switch).
    //
    // Fix: give lightweight-charts a private inner <div> that React never renders
    // and therefore never traverses. React only ever owns/removes the outer
    // container as an opaque leaf; the library mutates only its own inner node.
    const inner = document.createElement("div");
    inner.style.width = "100%";
    inner.style.height = "100%";
    host.appendChild(inner);

    // Theme-aware colors from the active palette's CSS tokens.
    const c = chartColors();
    const theme = {
      layout: { background: { type: ColorType.Solid, color: c.chartBg }, textColor: c.chartText },
      grid: { vertLines: { color: c.grid }, horzLines: { color: c.grid } },
      rightPriceScale: { borderColor: c.chartBorder },
      timeScale: { borderColor: c.chartBorder },
    };

    const chart = createChart(inner, {
      // Let lightweight-charts own the single (internal) ResizeObserver. Running
      // our OWN observer alongside it widened the dispose-during-resize race that
      // throws "Object is disposed" from fancy-canvas when widgets are dragged/
      // resized in the grid. autoSize keeps the chart sized to its container.
      autoSize: true,
      ...theme,
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      localization: {
        timeFormatter: (timestamp: number) =>
          new Date(timestamp * 1000).toLocaleString("fr-FR", {
            timeZone: "Europe/Paris",
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
          }),
      },
      timeScale: {
        ...(theme.timeScale ?? {}),
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (timestamp: number) =>
          new Date(timestamp * 1000).toLocaleTimeString("fr-FR", {
            timeZone: "Europe/Paris",
            hour: "2-digit",
            minute: "2-digit",
          }),
      },
    });
    chartRef.current = chart;

    const minMove = pricePrecision >= 5 ? 0.00001 : 0.01;
    const series = chart.addSeries(CandlestickSeries, {
      priceFormat: { type: "price", precision: pricePrecision, minMove },
      upColor: c.candleUp,
      downColor: c.candleDown,
      borderVisible: false,
      wickUpColor: c.candleUp,
      wickDownColor: c.candleDown,
    });
    seriesRef.current = series;
    setSeriesVersion((v) => v + 1);

    // Validate + set data
    const valid = data
      .filter((d) => {
        const { open, high, low, close } = d;
        return (
          !isNaN(open) && !isNaN(high) && !isNaN(low) && !isNaN(close) &&
          high >= low && high >= open && high >= close && low <= open && low <= close
        );
      })
      .sort((a, b) => a.time - b.time)
      .map((d) => ({ ...d, time: d.time as any }));

    series.setData(valid);
    chart.timeScale().fitContent();

    // Synchronous teardown: dispose the LOCAL `chart` (not chartRef, which a fast
    // remount may have repointed) and null the refs immediately so nothing else
    // touches a disposed instance. With autoSize on, lightweight-charts tears
    // down its own ResizeObserver inside remove(); we add no observer of our own.
    return () => {
      // Order matters: dispose the chart FIRST (lightweight-charts removes its own
      // canvases from `inner`), THEN remove our private `inner` node from the host.
      // Both are wrapped: a fast remount may already have torn either down. React
      // never sees `inner`, so it never double-removes the library's nodes.
      try { chart.remove(); } catch { /* already disposed */ }
      try { inner.remove(); } catch { /* already detached */ }
      if (chartRef.current === chart) {
        chartRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [data, isDark, pricePrecision, themeId]);

  return { chartRef, seriesRef, seriesVersion };
}
